import React from "react";
import { Card, CardContent, Typography, Button } from "@mui/material";
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Edit as EditIcon,
  Block as BlockIcon,
  CheckCircle as EnableIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { Employee } from "../../../validation/business.validation";
import ConfirmDeleteDialog from "../../../common/confirmDeleteAlert";
import ConfirmBlockDialog from "../../../common/confirmBlockAlert";
import { formatPhoneNumber } from "../../../helper/phoneFormatter.helper";

interface Props {
  employees: Employee[];
  onDelete: (emp: Employee) => void;
  onEdit: (emp: Employee) => void;
  onBlock: (emp: Employee) => void;
}

const EmployeeCardView: React.FC<Props> = ({ employees, onDelete, onEdit, onBlock }) => {
  const [openDeleteDialog, setOpenDeleteDialog] = React.useState(false);
  const [openStatusDialog, setOpenStatusDialog] = React.useState(false);
  const [selectedEmployee, setSelectedEmployee] = React.useState<Employee | null>(null);

  const handleDeleteConfirm = () => {
    if (selectedEmployee) {
      onDelete(selectedEmployee);
    }
    setOpenDeleteDialog(false);
    setSelectedEmployee(null);
  };

  const handleStatusConfirm = () => {
    if (selectedEmployee) {
      onBlock(selectedEmployee);
    }
    setOpenStatusDialog(false);
    setSelectedEmployee(null);
  };

  const openDeactivateDialog = (emp: Employee) => {
    setSelectedEmployee(emp);
    setOpenStatusDialog(true);
  };

  const openDeleteDialogHandler = (emp: Employee) => {
    setSelectedEmployee(emp);
    setOpenDeleteDialog(true);
  };

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 px-4 pb-6">
        {employees.map((emp, index) => {
          const isActive = emp.isActive;
          return (
            <Card key={index} className="rounded-2xl shadow border border-gray-200">
              <CardContent className={`flex flex-col items-center text-center px-4 py-6 gap-4 ${!isActive ? "bg-gray-200" : ""}`}>
                <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center shadow">
                  <PersonIcon className="text-gray-400 text-6xl" />
                </div>

                <Typography variant="h6" className="font-semibold text-gray-900">
                  {`${(emp as any).firstName || ''} ${(emp as any).middleName || ''} ${(emp as any).lastName || ''}`.trim() || (emp as any).name || "-"}
                </Typography>

                <div className="text-sm text-gray-700 space-y-2 text-left w-full">
                  <div className="flex items-center gap-2">
                    <EmailIcon fontSize="small" />
                    <span>
                      <strong>Email:</strong> {emp.email || "-"}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <PhoneIcon fontSize="small" />
                    <span>
                      <strong>Phone:</strong> {emp.phone ? formatPhoneNumber(emp.phone) : "-"}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 w-full mt-4">
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<EditIcon />}
                    color="secondary"
                    fullWidth
                    sx={{ borderRadius: 2, py: 0.5, fontWeight: 600 }}
                    onClick={() => onEdit(emp)}
                    className="md:col-span-2"
                  >
                    Edit
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={isActive ? <BlockIcon /> : <EnableIcon />}
                    color={isActive ? "warning" : "success"}
                    fullWidth
                    sx={{ borderRadius: 2, py: 0.5, fontWeight: 600 }}
                    onClick={() => openDeactivateDialog(emp)}
                  >
                    {isActive ? "Deactivate" : "Enable"}
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<DeleteIcon />}
                    color="error"
                    fullWidth
                    sx={{ borderRadius: 2, py: 0.5, fontWeight: 600 }}
                    onClick={() => openDeleteDialogHandler(emp)}
                  >
                    Delete
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <ConfirmDeleteDialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        onConfirm={handleDeleteConfirm}
        itemName={selectedEmployee?.name || ""}
      />

      <ConfirmBlockDialog
        open={openStatusDialog}
        isActive={!!selectedEmployee?.isActive}
        onClose={() => setOpenStatusDialog(false)}
        onConfirm={handleStatusConfirm}
        itemName={selectedEmployee?.name}
      />
    </>
  );
};

export default EmployeeCardView;
